'use client';

import { Button } from '@ahafood/ui/components/button';
import { Image } from '@ahafood/ui/components/image';
import { ScrollArea } from '@ahafood/ui/components/scroll-area';
import { assetPrefix } from '@ahm/api-wrappers-core';
import { cn } from '@ahm/common-helpers';
import React, { useCallback } from 'react';

import { StepIndicator } from './step-indicator';
import { trackEvent } from '@/lib/event-tracking';
import {
  DIET_TYPES,
  type DietType,
  useAIPlannerStore,
} from '@/store/ai-planner';

export function StepDietType() {
  const currentStep = useAIPlannerStore.use.currentStep();
  const preferences = useAIPlannerStore.use.preferences();
  const setPreferences = useAIPlannerStore.use.setPreferences();
  const nextStep = useAIPlannerStore.use.nextStep();
  const prevStep = useAIPlannerStore.use.prevStep();

  const handleDietSelect = useCallback(
    (diet: DietType) => {
      // Toggle: if clicking same diet, deselect it; otherwise select the new one
      const newDiet = preferences.dietType === diet ? undefined : diet;
      setPreferences({ dietType: newDiet });
    },
    [preferences.dietType, setPreferences]
  );

  return (
    <div className="p-0">
      {/* Background Image */}
      <div className="absolute -top-40 inset-x-0 w-full h-auto flex items-start justify-center py-4 aspect-square">
        <Image
          src={`${assetPrefix}/static/icons/ai-planner-page-2-bg.svg`}
          alt="planner diet type background"
          fill
          className="object-contain"
          priority
        />
      </div>

      <ScrollArea className="flex-1 w-full max-h-[calc(100dvh-123px-88px-24px)] overflow-auto">
        {/* Title */}
        <div className="mb-6 mt-4">
          <h2 className="text-2xl font-bold text-[#3B82F6] leading-tight">
            Chế độ ăn cho bữa trưa
          </h2>
          <h2 className="text-2xl font-bold leading-tight">
            <span className="text-white bg-primary-40 rounded-full px-3 w-fit inline-block">
              của bạn là gì?
            </span>
          </h2>
        </div>

        {/* Diet Types List */}
        <div className="space-y-3 mb-6 px-1">
          {DIET_TYPES.map((diet) => (
            <button
              key={diet.value}
              onClick={() => handleDietSelect(diet.value)}
              className={cn(
                'w-full relative rounded-3xl transition-all overflow-hidden h-[110px]',
                'hover:scale-y-[1.02] hover:shadow-lg',
                preferences.dietType === diet.value
                  ? 'ring-4 ring-primary-50/90 shadow-xl '
                  : 'hover:ring-2 hover:ring-white/30'
              )}
              style={{ backgroundColor: diet.color }}
            >
              <div className="flex items-center h-full">
                {/* Left Content */}
                <div className="flex-1 flex items-center py-3 pl-6 pr-2 z-10">
                  <div className="text-left">
                    <h3 className="text-white font-bold text-lg uppercase leading-tight">
                      {diet.label}
                    </h3>
                    <p className="text-white/95 text-[10px] font-medium mt-1 leading-relaxed">
                      {diet.description}
                    </p>
                  </div>
                </div>

                {/* Right Image */}
                <div className="shrink-0 w-[160px] h-full flex items-center justify-center py-2 pr-2">
                  <div className="relative size-full">
                    <Image
                      src={`${assetPrefix}/static/images/${diet.icon}`}
                      alt={diet.label}
                      fill
                      className="object-contain"
                      sizes="160px"
                    />
                  </div>
                </div>
              </div>

              {/* Subtle gradient overlay */}
              <div className="absolute inset-0 bg-gradient-to-r from-black/5 to-transparent pointer-events-none" />

              {/* Selection indicator */}
              {preferences.dietType === diet.value && (
                <div className="absolute top-4 right-4 size-6 bg-white rounded-full flex items-center justify-center shadow-lg z-20">
                  <svg
                    className="size-4 text-green-50"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={3}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                </div>
              )}
            </button>
          ))}
        </div>

        {/* Spacer for bottom button */}
        <div className="h-24" />
      </ScrollArea>

      {/* Navigation Buttons - Fixed at bottom */}
      <div className="fixed bottom-0 inset-x-0 pb-4 pt-2 px-4">
        <div className="glass-bubble rounded-full flex justify-between items-center px-4 py-2 h-16">
          <Button
            variant="ghost"
            onClick={() => {
              trackEvent('meal_plan', {
                button_name: 'meal_plan_back',
                button_location: currentStep,
              });
              prevStep();
            }}
            className="text-white hover:bg-white/10 flex items-center gap-2 text-sm font-semibold"
          >
            ← Quay lại
          </Button>
          <StepIndicator />
          <Button
            variant="ghost"
            onClick={() => {
              trackEvent('meal_plan', {
                event_name: 'click_button',
                button_name: 'meal_plan_step',
                button_location: currentStep,
              });
              nextStep();
            }}
            className="text-white hover:bg-white/10 flex items-center gap-2 text-sm font-semibold"
          >
            Tiếp tục →
          </Button>
        </div>
      </div>
    </div>
  );
}
